import React, { useCallback, useEffect, useState } from 'react';
import { Button, Drawer, Popconfirm, Row, Space, Table, Typography, Card, Tooltip, Tag, Select, DatePicker, Input, Switch } from 'antd';
import type { TableProps } from 'antd';
import type { FilterValue, SortOrder, SorterResult, TablePaginationConfig } from 'antd/es/table/interface';
import { openNotification } from '../../../utils';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { APP_LOCAL_DATE_FORMAT } from 'config/constants';
import { listMeasuringInstrumentInventorys, deleteMeasuringInstrumentInventory, updateMeasuringInstrumentInventory, listMeasuringInstrumentInventorysAll } from '../services/measuring-instrument-inventoryService';
import { listMeasuringInstrumentsAll } from '../../measuring-instruments/services/measuring-instrumentService';
import { listInspectorsAll } from '../../inspectors/services/inspectorService';
import TrashIcon from 'assets/icons/trash.svg';
import PenIcon from 'assets/icons/pen.svg';
import { SearchOutlined, FileExcelOutlined } from '@ant-design/icons';
import { debounce } from 'lodash';
import MeasuringInstrumentInventoryForm from './Form';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

export default function MeasuringInstrumentInventoryList({ referenceId }: { referenceId?: number }) {
  const [data, setData] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | undefined>();
  const [exporting, setExporting] = useState(false);
  // Filters
  const [filterOpen, setFilterOpen] = useState(false);
  const [miOptions, setMiOptions] = useState<any[]>([]);
  const [inspectorOptions, setInspectorOptions] = useState<any[]>([]);
  // Committed filters (applied)
  const [selectedMiIds, setSelectedMiIds] = useState<number[]>([]);
  const [isActiveFilter, setIsActiveFilter] = useState<boolean | undefined>(undefined);
  const [selectedAccountId, setSelectedAccountId] = useState<number | undefined>(undefined);
  const [calFrom, setCalFrom] = useState<string | undefined>(undefined);
  const [calTo, setCalTo] = useState<string | undefined>(undefined);
  const [nextCalFrom, setNextCalFrom] = useState<string | undefined>(undefined);
  const [nextCalTo, setNextCalTo] = useState<string | undefined>(undefined);
  // Search
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const debouncedApplySearch = React.useMemo(() => debounce((val: string) => { setSearch(val); }, 300), []);
  React.useEffect(() => () => { debouncedApplySearch.cancel(); }, [debouncedApplySearch]);
  // Pending filters (in drawer, only applied on "Uygula")
  const [pendingMiIds, setPendingMiIds] = useState<number[]>([]);
  const [pendingIsActive, setPendingIsActive] = useState<boolean | undefined>(undefined);
  const [pAccountId, setPAccountId] = useState<number | undefined>(undefined);
  const [pCalFrom, setPCalFrom] = useState<any>(null);
  const [pCalTo, setPCalTo] = useState<any>(null);
  const [pNextCalFrom, setPNextCalFrom] = useState<any>(null);
  const [pNextCalTo, setPNextCalTo] = useState<any>(null);


  const DEFAULT_SORT_COLUMN = 'id';
  const DEFAULT_SORT_ORDER: SortOrder = 'ascend';
  const [sortState, setSortState] = useState<{ columnKey?: string; order?: SortOrder }>({
    columnKey: DEFAULT_SORT_COLUMN,
    order: DEFAULT_SORT_ORDER
  });
  const resolveSortField = (columnKey?: string) => {
    switch (columnKey) {
      case 'id':
        return 'id';
      case 'measuringInstrumentName':
        return 'measuringInstrument.name';
      case 'serialNumber':
        return 'serialNumber';
      case 'brand':
        return 'brand';
      case 'model':
        return 'model';
      case 'calibrationDate':
        return 'calibrationDate';
      case 'nextCalibrationDate':
        return 'nextCalibrationDate';
      case 'isActive':
        return 'isActive';
      default:
        return undefined;
    }
  };
  const resolveSortParam = () => {
    if (!sortState.columnKey || !sortState.order) return `${DEFAULT_SORT_COLUMN},asc`;
    const field = resolveSortField(sortState.columnKey);
    if (!field) return undefined;
    const direction = sortState.order === 'descend' ? 'desc' : 'asc';
    return `${field},${direction}`;
  };
  const buildFilterQuery = useCallback(() => {
    const filters: string[] = [];
    if (selectedMiIds.length) filters.push('measuringInstrumentId.in=' + selectedMiIds.join(','));
    if (typeof isActiveFilter === 'boolean') filters.push('isActive.equals=' + isActiveFilter);
    if (selectedAccountId !== undefined) filters.push('accountId.equals=' + selectedAccountId);
    if (calFrom) filters.push('calibrationDate.greaterThanOrEqual=' + calFrom);
    if (calTo) filters.push('calibrationDate.lessThanOrEqual=' + calTo);
    if (nextCalFrom) filters.push('nextCalibrationDate.greaterThanOrEqual=' + nextCalFrom);
    if (nextCalTo) filters.push('nextCalibrationDate.lessThanOrEqual=' + nextCalTo);
    if (search && search.length >= 2) filters.push('search.contains=' + encodeURIComponent(search));
    return filters.join('&');
  }, [calFrom, calTo, isActiveFilter, nextCalFrom, nextCalTo, search, selectedAccountId, selectedMiIds]);
  const fetchData = useCallback(async () => {
    const query = buildFilterQuery();
    const sortParam = resolveSortParam();
    const res = await listMeasuringInstrumentInventorys(page - 1, pageSize, sortParam, query);
    setData(res.content);
    setTotal(res.totalElements);
  }, [buildFilterQuery, page, pageSize, sortState.columnKey, sortState.order]);
  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { listMeasuringInstrumentsAll().then(setMiOptions); listInspectorsAll().then(setInspectorOptions); }, []);
  const openCreate = () => { setEditingId(undefined); setOpen(true); };
  const openFilter = () => {
    setPendingMiIds(selectedMiIds);
    setPendingIsActive(isActiveFilter);
    setPAccountId(selectedAccountId);
    setPCalFrom(calFrom ? dayjs(calFrom) : null);
    setPCalTo(calTo ? dayjs(calTo) : null);
    setPNextCalFrom(nextCalFrom ? dayjs(nextCalFrom) : null);
    setPNextCalTo(nextCalTo ? dayjs(nextCalTo) : null);
    setFilterOpen(true);
  };
  const closeFilter = () => setFilterOpen(false);
  const clearFilter = () => {
    setPendingMiIds([]);
    setPendingIsActive(undefined);
    setPAccountId(undefined);
    setPCalFrom(null);
    setPCalTo(null);
    setPNextCalFrom(null);
    setPNextCalTo(null);
  };
  const openEdit = (id: number) => { setEditingId(id); setOpen(true); };
  const handleClose = () => setOpen(false);
  const handleDelete = async (id: number) => {
    try {
      await deleteMeasuringInstrumentInventory(id);
      openNotification('success', 'Başarılı', 'Ölçüm Cihazı Envanteri Silindi');
      fetchData();
    } catch (e) {
      openNotification('error', 'Hata', 'Ölçüm Cihazı Envanteri silme işlemi başarısız');
    }
  };
  // Form'daki update ile aynı şemayı yakalamak için payload dönüştürücü
  const buildUpdatePayload = (row: any, isActiveVal: boolean) => {
    const toId = (v: any) => (typeof v === 'object' && v !== null ? v.id : v);
    const measuringInstrumentId = toId(row?.measuringInstrument);
    const accountId = toId(row?.account);
    const calibrationDate = row?.calibrationDate ? dayjs(row.calibrationDate).format('YYYY-MM-DD') : undefined;
    const nextCalibrationDate = row?.nextCalibrationDate ? dayjs(row.nextCalibrationDate).format('YYYY-MM-DD') : undefined;
    const payload: any = {
      measuringInstrument: measuringInstrumentId,
      serialNumber: row?.serialNumber ?? '',
      brand: row?.brand ?? '',
      model: row?.model ?? '',
      isActive: isActiveVal,
    };
    if (calibrationDate) payload.calibrationDate = calibrationDate;
    if (nextCalibrationDate) payload.nextCalibrationDate = nextCalibrationDate;
    if (row?.deviceFeatures !== undefined) payload.deviceFeatures = row.deviceFeatures;
    if (row?.purchaseOrganization !== undefined) payload.purchaseOrganization = row.purchaseOrganization;
    if (row?.purchaseDate) payload.purchaseDate = dayjs(row.purchaseDate).format('YYYY-MM-DD');
    else if (row?.purchaseDate === null) payload.purchaseDate = null;
    if (row?.warrantyValidUntil) payload.warrantyValidUntil = dayjs(row.warrantyValidUntil).format('YYYY-MM-DD');
    else if (row?.warrantyValidUntil === null) payload.warrantyValidUntil = null;
    if (row?.acceptableErrorTolerance !== undefined && row?.acceptableErrorTolerance !== null) {
      payload.acceptableErrorTolerance = Number(row.acceptableErrorTolerance);
    } else {
      payload.acceptableErrorTolerance = null;
    }
    if (accountId) payload.account = { id: accountId };
    return payload;
  };
  const activeSortKey = sortState.columnKey;
  const activeSortOrder = sortState.order;
  const resolveSortOrder = (key: string) => (activeSortKey === key ? activeSortOrder : undefined);
  const formatDateCell = (v?: string) => (v ? dayjs(v).format(APP_LOCAL_DATE_FORMAT) : '');
  const handleExportExcel = useCallback(async () => {
    try {
      setExporting(true);
      const query = buildFilterQuery();
      const sortParam = resolveSortParam();
      const list = await listMeasuringInstrumentInventorysAll(sortParam, query);
      const headers = [
        'Ölçüm Cihazı',
        'Seri Numarası',
        'Marka',
        'Model',
        'PKM Uzmanı',
        'Kalibrasyon Tarihi',
        'Sonraki Kalibrasyon Tarihi',
      ];
      const rows = Array.isArray(list) ? list.map((item: any) => {
        const instrumentName = item?.measuringInstrument?.name ?? '';
        const account = item?.account || {};
        const fullName = [account.firstName, account.lastName].filter(Boolean).join(' ').trim() || account.email || '';
        return [
          instrumentName,
          item?.serialNumber ?? '',
          item?.brand ?? '',
          item?.model ?? '',
          fullName,
          formatDateCell(item?.calibrationDate),
          formatDateCell(item?.nextCalibrationDate),
        ];
      }) : [];
      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Olcum Cihazi Envanteri');
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
      saveAs(blob, 'olcum-cihazi-envanteri.xlsx');
    } catch (error) {
      console.error('Measuring instrument inventories excel export error:', error);
      openNotification('error', 'Hata', 'Excel dışa aktarılırken bir hata oluştu');
    } finally {
      setExporting(false);
    }
  }, [buildFilterQuery, sortState.columnKey, sortState.order]);
  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', sorter: true, sortOrder: resolveSortOrder('id') },
    {
      title: 'Ölçüm Cihazı',
      dataIndex: 'measuringInstrument.name',
      key: 'measuringInstrumentName',
      sorter: true,
      sortOrder: resolveSortOrder('measuringInstrumentName'),
      render: (_: any, r: any) => r?.measuringInstrument?.name
    },
    { title: 'Marka', dataIndex: 'brand', key: 'brand', sorter: true, sortOrder: resolveSortOrder('brand') },
    { title: 'Model', dataIndex: 'model', key: 'model', sorter: true, sortOrder: resolveSortOrder('model') },
    { title: 'Seri Numarası', dataIndex: 'serialNumber', key: 'serialNumber', sorter: true, sortOrder: resolveSortOrder('serialNumber') },
    // {
    //   title: 'Cihaz Özellikleri',
    //   dataIndex: 'deviceFeatures',
    //   sorter: false,
    //   render: (v: string) => {
    //     if (!v) return '';
    //     const short = v.length > 40 ? v.slice(0, 40) + '…' : v;
    //     return <Tooltip title={v}>{short}</Tooltip>;
    //   }
    // },
    // { title: 'Satın Alınan Kuruluş', dataIndex: 'purchaseOrganization', sorter: true },
    // { title: 'Satın Alınma Tarihi', dataIndex: 'purchaseDate', sorter: true, render: (v: string) => v ? dayjs(v).format(APP_LOCAL_DATE_FORMAT) : '' },
    // { title: 'Garanti Geçerlilik', dataIndex: 'warrantyValidUntil', sorter: true, render: (v: string) => v ? dayjs(v).format(APP_LOCAL_DATE_FORMAT) : '' },
    // {
    //   title: 'Hata Toleransı (%)',
    //   dataIndex: 'acceptableErrorTolerance',
    //   sorter: true,
    //   render: (v: number) => (v === undefined || v === null) ? '' : Number(v).toFixed(2)
    // },
    {
      title: 'PKM Uzmanı', dataIndex: 'account', sorter: false, render: (_: any, r: any) => {
        const acc: any = r?.account;
        if (!acc) return '';
        const fullName = [acc.firstName, acc.lastName].filter(Boolean).join(' ').trim();
        return fullName || acc.email || '';
      }
    },
    { title: 'Kalibrasyon Tarihi', dataIndex: 'calibrationDate', key: 'calibrationDate', sorter: true, sortOrder: resolveSortOrder('calibrationDate'), render: (v: string) => v ? dayjs(v).format(APP_LOCAL_DATE_FORMAT) : '' },
    { title: 'Kalibrasyon Geçerlilik Tarihi', dataIndex: 'nextCalibrationDate', key: 'nextCalibrationDate', sorter: true, sortOrder: resolveSortOrder('nextCalibrationDate'), render: (v: string) => v ? dayjs(v).format(APP_LOCAL_DATE_FORMAT) : '' },
    {
      title: 'Durum',
      dataIndex: 'isActive',
      key: 'isActive',
      sorter: true,
      sortOrder: resolveSortOrder('isActive'),
      render: (v: boolean, r: any) => (
        <Switch
          checkedChildren='Aktif'
          unCheckedChildren='Pasif'
          checked={!!v}
          onChange={async (val) => {
            try {
              const body = buildUpdatePayload(r, val);
              await updateMeasuringInstrumentInventory(r.id, body);
              setData(prev => prev.map(item => item.id === r.id ? ({ ...item, isActive: val }) : item));
              openNotification('success', 'Başarılı', val ? 'Ölçüm cihazı aktif yapıldı' : 'Ölçüm cihazı pasif yapıldı');
            } catch (e) {
              openNotification('error', 'Hata', 'Durum güncellenemedi.');
            }
          }}
        />
      )
    },
    {
      title: 'İşlemler', render: (_: any, r: any) => (
        <Space>
          <Link to={'/measuring-instrument-inventories/' + r.id}>
            <Tooltip title='Düzenle'>
              <Button
                icon={<img src={PenIcon} alt="pen" className="icon" />}
                shape="circle"
                className="icon-button"
              />
            </Tooltip>
          </Link>
          <Popconfirm title='Sil?' onConfirm={() => handleDelete(r.id)}>
            <Tooltip title='Sil'>
              <Button
                icon={<img src={TrashIcon} alt="trash" className="icon" />}
                shape="circle"
                className="icon-button ml-12px"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];
  const handleTableChange: TableProps<any>['onChange'] = (
    _pagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter: SorterResult<any> | SorterResult<any>[]
  ) => {
    const normalizedSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    const nextOrder = (normalizedSorter?.order as SortOrder | undefined) ?? undefined;
    const rawKey = (normalizedSorter?.columnKey ?? normalizedSorter?.field) as string | undefined;
    const nextKey = nextOrder ? rawKey : undefined;
    if ((sortState.columnKey ?? undefined) === (nextKey ?? undefined) && sortState.order === nextOrder) {
      return;
    }
    setSortState({ columnKey: nextKey, order: nextOrder });
    setPage(1);
  };
  return (
    <>

      <Card
        className='br-30px kontrolx-card'
        title={(
          <Row align='bottom'>
            <Typography.Title level={4} style={{ margin: 0 }}>Ölçüm Cihazı Envanteri</Typography.Title>
            <Typography.Text type='secondary' className='ms-5' style={{ fontWeight: 500 }}>
              <strong>({total})</strong> kayıt listelendi
            </Typography.Text>
          </Row>
        )}
        extra={
          <Space>
            <Input
              allowClear
              placeholder='Ara'
              prefix={<SearchOutlined style={{ color: '#b8b9bc' }} />}
              className='d-input d-input-form d-input-filter'
              style={{ width: 240 }}
              value={searchInput}
              onChange={(e) => {
                const val = e.target.value || '';
                setSearchInput(val);
                if (val.length >= 2) {
                  debouncedApplySearch(val);
                } else if (val.length === 0) {
                  debouncedApplySearch('');
                }
              }}
            />
            <Tooltip title="Dışa Aktar">
              <Button
                shape='circle'
                className='excel-icon-button'
                onClick={handleExportExcel}
                aria-label='Excel indir'
              >
                <FileExcelOutlined />
              </Button>
            </Tooltip>
            <Button type='primary' onClick={openFilter}>Filtrele</Button>
            <Button type='primary' onClick={openCreate}>Ekle</Button>
          </Space>
        }
      >
        <Table
          rowKey='id'
          dataSource={data}
          columns={columns}
          onChange={handleTableChange}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: ['50', '100', '200', '500'],
            onChange: (p, s) => { setPage(p); setPageSize(s); }
          }}
        />
      </Card>
      <Drawer open={open} onClose={handleClose} destroyOnClose width={480} title={editingId ? 'Ölçüm Cihazı Envanteri Düzenle' : 'Envantere Ölçüm Cihazı Ekle'}>
        <MeasuringInstrumentInventoryForm id={editingId} onSuccess={() => { handleClose(); fetchData(); }} stack />
      </Drawer>
      <Drawer open={filterOpen} onClose={closeFilter} destroyOnClose width={420} title='Filtre'>
        <Space direction='vertical' style={{ width: '100%' }} size='large'>
          <div>
            <Typography.Text>Ölçüm Cihazı</Typography.Text>
            <Select
              mode='multiple'
              allowClear
              showSearch
              optionFilterProp='children'
              placeholder='Seçiniz'
              style={{ width: '100%', marginTop: 8 }}
              value={pendingMiIds}
              onChange={(vals: number[]) => setPendingMiIds(vals)}
              filterOption={(input, option) => ((option?.children?.toString()) || '').toLocaleLowerCase('tr').includes(input.toLocaleLowerCase('tr'))}
            >
              {miOptions.map((o: any) => (
                <Select.Option key={o.id} value={o.id}>{o.name}</Select.Option>
              ))}
            </Select>
          </div>
          <div>
            <Typography.Text>P.K.M Uzmanı</Typography.Text>
            <Select
              allowClear
              showSearch
              placeholder='Seçiniz'
              style={{ width: '100%', marginTop: 8 }}
              value={pAccountId}
              onChange={(val: number | undefined) => setPAccountId(val)}
              filterOption={(input, option) => ((option?.children?.toString()) || '').toLocaleLowerCase('tr').includes(input.toLocaleLowerCase('tr'))}
            >
              {inspectorOptions.map((ins: any) => {
                const acc = ins?.account || {};
                const full = [acc.firstName, acc.lastName].filter(Boolean).join(' ').trim();
                const label = full || acc.email || `#${ins.id}`;
                const accountId = acc?.id;
                if (!accountId) return null;
                return (
                  <Select.Option key={ins.id} value={accountId}>{label}</Select.Option>
                );
              })}
            </Select>
          </div>
          <div>
            <Typography.Text>Kalibrasyon Tarihi</Typography.Text>
            <DatePicker.RangePicker
              style={{ width: '100%', marginTop: 6 }}
              value={[pCalFrom, pCalTo] as any}
              onChange={(vals) => { setPCalFrom(vals?.[0] || null); setPCalTo(vals?.[1] || null); }}
              format={APP_LOCAL_DATE_FORMAT}
            />
          </div>
          <div>
            <Typography.Text>Kalibrasyon Geçerlilik Tarihi</Typography.Text>
            <DatePicker.RangePicker
              style={{ width: '100%', marginTop: 6 }}
              value={[pNextCalFrom, pNextCalTo] as any}
              onChange={(vals) => { setPNextCalFrom(vals?.[0] || null); setPNextCalTo(vals?.[1] || null); }}
              format={APP_LOCAL_DATE_FORMAT}
            />
          </div>

          <div>
            <Typography.Text>Durum</Typography.Text>
            <Select
              style={{ width: '100%', marginTop: 8 }}
              value={pendingIsActive === undefined ? 'all' : pendingIsActive ? 'true' : 'false'}
              onChange={(val: 'all' | 'true' | 'false') => {
                if (val === 'all') setPendingIsActive(undefined);
                else if (val === 'true') setPendingIsActive(true);
                else setPendingIsActive(false);
              }}
              options={[
                { value: 'all', label: 'Tümü' },
                { value: 'true', label: 'Aktif' },
                { value: 'false', label: 'Pasif' },
              ]}
            />
          </div>
          <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
            <Button onClick={clearFilter}>Temizle</Button>
            <Button type='primary' onClick={() => {
              setSelectedMiIds(pendingMiIds);
              setIsActiveFilter(pendingIsActive);
              setSelectedAccountId(pAccountId);
              setCalFrom(pCalFrom ? pCalFrom.format('YYYY-MM-DD') : undefined);
              setCalTo(pCalTo ? pCalTo.format('YYYY-MM-DD') : undefined);
              setNextCalFrom(pNextCalFrom ? pNextCalFrom.format('YYYY-MM-DD') : undefined);
              setNextCalTo(pNextCalTo ? pNextCalTo.format('YYYY-MM-DD') : undefined);
              closeFilter();
            }}>Uygula</Button>
          </Space>
        </Space>
      </Drawer>
    </>
  );
}
